// @flow

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST } from '../../../store/actions';
import type { RootState, InternationalPhone } from '../../../store/types';
import { API } from '../../../utils/APIUtils';
import { compare, filterOptions } from '../../../utils/CommonUtils';
import { sizes, fontFamily, colors } from '../../../styles/GlobalStyle';
import { StyledAutoComplete } from '../../../styles/PlacesStyle';
import { Icon } from '../../../modules/shared/components/Icon';

import TextField from '@material-ui/core/TextField';
// https://github.com/moroshko/autosuggest-highlight
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { Licenses } from 'types/common';
import config from 'config/app.config';

type InputCountryProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  onChange: Function,
  setCountryCode: Function,
  defaultCountryValue?: string,
  isResidency?: boolean,
|};

const InputCountryRaw = forwardRef<InputCountryProps, HTMLInputElement>(
  (
    {
      className,
      name,
      label,
      id,
      invalid = false,
      onChange,
      setCountryCode,
      defaultCountryValue,
      isResidency,
    },
    ref
  ) => {
    const innerRef = useRef(null);
    const [countries, setCountries] = useState<Array<InternationalPhone>>([]);
    const [inputValue, setInputValue] = useState('');
    const dispatch = useDispatch();
    const i18n = useSelector((state: RootState) => state.i18n);
    const userRegistration = useSelector((state: RootState) => state.userRegistration);

    useImperativeHandle(ref, () => innerRef.current);

    const clearInput = useCallback(() => {
      setInputValue('');
      onChange && onChange('');
      setCountryCode('');
    }, [setInputValue]);

    const preventEnter = useCallback((event) => {
      if ((event.charCode || event.keyCode) === 13) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, []);

    const handleFocus = useCallback((event) => {
      // https://stackoverflow.com/a/61973547
      event.target.setAttribute('autocomplete', 'new-password');
    });

    useEffect(() => {
      dispatch({
        type: WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST,
        payload: {
          method: API.ListInternationalCountryCallingCodes,
          params: {
            lang: i18n.language,
          },
        },
      });
    }, [dispatch, i18n.language]);

    useEffect(() => {
      if (userRegistration.listInternationalCountryCallingCodes.response) {
        const { response } = userRegistration.listInternationalCountryCallingCodes;
        const allCountries = response.countries;

        if (isResidency && config.license === Licenses.DE) {
          setCountries(allCountries.filter((country) => country.alpha2Code === Licenses.DE));
        } else if (isResidency) {
          setCountries(allCountries.filter((country) => country.allowResidency).sort(compare));
        } else {
          setCountries(allCountries.sort(compare));
        }
      }
    }, [
      userRegistration.listInternationalCountryCallingCodes,
      userRegistration.listInternationalCountryCallingCodes.response,
    ]);
    // countryInputValue comes from parent
    // Used to autofill country value based on city
    useEffect(() => {
      if (defaultCountryValue && countries) {
        // We get the inputValue directly if it's set from parent component
        onChange(defaultCountryValue);
        setInputValue(defaultCountryValue);
      }
    }, [defaultCountryValue, countries]);

    return (
      <span className={`${className || ''}${invalid ? ' invalid' : ''}`}>
        <StyledAutoComplete
          label={label}
          disablePortal={true}
          onKeyDown={(event) => {
            preventEnter(event);
          }}
          onFocus={handleFocus}
          onChange={(event, newValue) => {
            const newValueLabel = newValue ? newValue.label : '';
            // Manually trigger autocomplete onChanged based on parent onChange prop
            setInputValue(newValueLabel);
            setCountryCode(newValue.alpha2Code || '');
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
            // Manually trigger autocomplete onChanged based on parent onChange prop
            if (onChange) {
              onChange(newInputValue);
            }
          }}
          id={`${id}`}
          options={countries}
          filterOptions={filterOptions}
          getOptionLabel={(option) => option.label || ''} // Need to pass empty string instead of undefined to keep component as controlled
          renderOption={(option, { inputValue }) => {
            const matches = match(option.label, inputValue);
            const parts = parse(option.label, matches);

            return (
              <div>
                {parts.map((part, index) => (
                  <span key={index} style={{ color: part.highlight ? colors.white : '' }}>
                    {part.text}
                  </span>
                ))}
              </div>
            );
          }}
          renderInput={(params) => {
            return (
              <>
                <TextField {...params} label={`${label}`} name={`${name}`} />
                {inputValue && inputValue.length !== 0 && (
                  <div
                    className="input-clear"
                    onClick={clearInput}
                    role="button"
                    tabIndex={-1}
                    onKeyUp={() => {}}
                  >
                    <Icon name="close" />
                  </div>
                )}
              </>
            );
          }}
        />
      </span>
    );
  }
);

InputCountryRaw.displayName = 'InputCountry';

export const InputCountry: StyledComponent<InputCountryProps, {}, typeof InputCountryRaw> = styled(
  InputCountryRaw
)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  font-weight: ${fontFamily.primary.black.fontWeight};

  .suggestions {
    position: absolute;
    width: 100%;
    top: 100px;
    background-color: ${colors.black};
    z-index: 5;
  }

  .input-clear {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    cursor: pointer;
    outline: 0;
    transition: 300ms all ease-out;
    animation: scaleIn 0.3s ease-out;
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      color: #fff;
    }
  }

  .suggestion {
    margin-bottom: 5px;
  }
`;
